import { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { SuperAdminRentChargeListItemDto } from '@/features/superAdmin/features/data/types/superAdmin-data.types';
import {
    formatBoolean,
    formatDateOnly,
    formatDateTime,
    formatNavValue,
} from '@/features/superAdmin/features/data/shared/utils/superAdminDataFormatters';
import { SuperAdminRentChargeInfoDto } from '@/features/superAdmin/features/data/types/superAdmin-data.types';

const formatDecimal = (value?: number | null) => {
    if (value === null || value === undefined) return '';
    return String(value);
};

export const useSuperAdminRentChargesPageColumns = () => {
    const { t } = useTranslation();

    const columns: ColumnDef<SuperAdminRentChargeListItemDto>[] = [
        { accessorKey: 'id', header: t('superAdmin.data.columns.id') },
        {
            accessorKey: 'tenantName',
            header: t('superAdmin.data.columns.tenant'),
            cell: ({ row }) => formatNavValue(row.original.tenantId, row.original.tenantName),
        },
        {
            accessorKey: 'documentNumber',
            header: t('superAdmin.data.columns.documentNumber'),
        },
        {
            accessorKey: 'documentDate',
            header: t('superAdmin.data.columns.documentDate'),
            cell: ({ row }) => formatDateTime(row.original.documentDate),
        },
        {
            accessorKey: 'externalId',
            header: t('superAdmin.data.columns.externalId'),
        },
        {
            accessorKey: 'deletionMark',
            header: t('superAdmin.data.columns.deletionMark'),
            cell: ({ row }) => formatBoolean(row.original.deletionMark),
        },
    ];

    return columns;
};

export const useRentChargeInfoNestedColumns = () => {
    const { t } = useTranslation();

    const columns: ColumnDef<SuperAdminRentChargeInfoDto>[] = [
        { accessorKey: 'id', header: t('superAdmin.data.columns.id') },
        {
            accessorKey: 'hotelName',
            header: t('superAdmin.data.columns.hotel'),
            cell: ({ row }) => row.original.hotelName || row.original.hotelId,
        },
        {
            accessorKey: 'apartmentName',
            header: t('superAdmin.data.columns.room'),
            cell: ({ row }) => row.original.apartmentName || row.original.apartmentId,
        },
        {
            accessorKey: 'ownerName',
            header: t('superAdmin.data.columns.owner'),
            cell: ({ row }) => row.original.ownerName || row.original.ownerId,
        },
        {
            accessorKey: 'month',
            header: t('superAdmin.data.rentCharges.columns.month'),
            cell: ({ row }) => formatDateOnly(row.original.month),
        },
        {
            accessorKey: 'roomsRented',
            header: t('superAdmin.data.rentCharges.columns.roomsRented'),
            cell: ({ row }) => formatDecimal(row.original.roomsRented),
        },
        {
            accessorKey: 'revenue',
            header: t('superAdmin.data.rentCharges.columns.revenue'),
            cell: ({ row }) => formatDecimal(row.original.revenue),
        },
        {
            accessorKey: 'revenueVat',
            header: t('superAdmin.data.rentCharges.columns.revenueVat'),
            cell: ({ row }) => formatDecimal(row.original.revenueVat),
        },
        {
            accessorKey: 'electricity',
            header: t('superAdmin.data.rentCharges.columns.electricity'),
            cell: ({ row }) => formatDecimal(row.original.electricity),
        },
        {
            accessorKey: 'water',
            header: t('superAdmin.data.rentCharges.columns.water'),
            cell: ({ row }) => formatDecimal(row.original.water),
        },
        {
            accessorKey: 'internet',
            header: t('superAdmin.data.rentCharges.columns.internet'),
            cell: ({ row }) => formatDecimal(row.original.internet),
        },
        {
            accessorKey: 'comissions',
            header: t('superAdmin.data.rentCharges.columns.comissions'),
            cell: ({ row }) => formatDecimal(row.original.comissions),
        },
        {
            accessorKey: 'incomeTax',
            header: t('superAdmin.data.rentCharges.columns.incomeTax'),
            cell: ({ row }) => formatDecimal(row.original.incomeTax),
        },
        {
            accessorKey: 'total',
            header: t('superAdmin.data.rentCharges.columns.total'),
            cell: ({ row }) => formatDecimal(row.original.total),
        },
        {
            accessorKey: 'adGas',
            header: t('superAdmin.data.rentCharges.columns.adGas'),
            cell: ({ row }) => formatDecimal(row.original.adGas),
        },
        {
            accessorKey: 'adPropertyManagement',
            header: t('superAdmin.data.rentCharges.columns.adPropertyManagement'),
            cell: ({ row }) => formatDecimal(row.original.adPropertyManagement),
        },
        {
            accessorKey: 'adAgentCommission',
            header: t('superAdmin.data.rentCharges.columns.adAgentCommission'),
            cell: ({ row }) => formatDecimal(row.original.adAgentCommission),
        },
        {
            accessorKey: 'utilities',
            header: t('superAdmin.data.rentCharges.columns.utilities'),
            cell: ({ row }) => formatDecimal(row.original.utilities),
        },
        {
            accessorKey: 'previousMonthUtilities',
            header: t('superAdmin.data.rentCharges.columns.previousMonthUtilities'),
            cell: ({ row }) => formatDecimal(row.original.previousMonthUtilities),
        },
        {
            accessorKey: 'totalAfterPropertyManagement',
            header: t('superAdmin.data.rentCharges.columns.totalAfterPropertyManagement'),
            cell: ({ row }) => formatDecimal(row.original.totalAfterPropertyManagement),
        },
        {
            accessorKey: 'startingBalance',
            header: t('superAdmin.data.rentCharges.columns.startingBalance'),
            cell: ({ row }) => formatDecimal(row.original.startingBalance),
        },
        {
            accessorKey: 'paidToOwnerSum',
            header: t('superAdmin.data.rentCharges.columns.paidToOwnerSum'),
            cell: ({ row }) => formatDecimal(row.original.paidToOwnerSum),
        },
        {
            accessorKey: 'totalCurrentMonth',
            header: t('superAdmin.data.rentCharges.columns.totalCurrentMonth'),
            cell: ({ row }) => formatDecimal(row.original.totalCurrentMonth),
        },
        {
            accessorKey: 'currentBalance',
            header: t('superAdmin.data.rentCharges.columns.currentBalance'),
            cell: ({ row }) => formatDecimal(row.original.currentBalance),
        },
        {
            accessorKey: 'grossRevenue',
            header: t('superAdmin.data.rentCharges.columns.grossRevenue'),
            cell: ({ row }) => formatDecimal(row.original.grossRevenue),
        },
        {
            accessorKey: 'deduction',
            header: t('superAdmin.data.rentCharges.columns.deduction'),
            cell: ({ row }) => formatDecimal(row.original.deduction),
        },
        {
            accessorKey: 'transferredFunds',
            header: t('superAdmin.data.rentCharges.columns.transferredFunds'),
            cell: ({ row }) => formatDecimal(row.original.transferredFunds),
        },
    ];

    return columns;
};
